import { lazy, Suspense, useEffect } from 'react'
import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useAuth } from './hooks/useAuth'
import { Layout } from './components/Layout'
import { trackPageview } from './lib/analytics'
import { rememberReturnTo } from './lib/returnTo'
import { LoadingSpinner } from './components/LoadingSpinner'
import { useKidMode } from './contexts/KidModeContext'
import { KidRouteGuard } from './components/KidRouteGuard'
import { usePwaInstallAnalytics } from './hooks/usePwaInstallAnalytics'
import { OfflineBootstrap } from './components/OfflineBootstrap'
import LearnVerses from './pages/LearnVerses'
import LearnPractice from './pages/LearnPractice'
import LearnStats from './pages/LearnStats'

// Route-level code splitting: each page is fetched on first navigation, not
// in the initial bundle. Vite gives every dynamic import its own chunk; the
// browser then only downloads what the user actually visits.
//
// Learn tab pages are static: they are nested under LearnPage, and the layout
// Suspense uses key={pathname}. A lazy nested tab would suspend that parent
// boundary on first visit to Practice/Stats and replace the Learn shell with
// RouteFallback. LearnPage itself stays lazy.
const HomePage = lazy(() => import('./pages/HomePage'))
const KidHomePage = lazy(() => import('./pages/KidHomePage').then((m) => ({ default: m.KidHomePage })))
const AuthPage = lazy(() => import('./pages/AuthPage'))
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage'))
const QuranReaderPage = lazy(() => import('./pages/QuranReaderPage'))
const KidReaderStage = lazy(() =>
  import('./pages/KidReaderStage').then((m) => ({ default: m.KidReaderStage })),
)
const BookmarksPage = lazy(() => import('./pages/BookmarksPage'))
const BookmarkSurahPage = lazy(() => import('./pages/BookmarkSurahPage'))
const ProgressPage = lazy(() => import('./pages/ProgressPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))
const NotesPage = lazy(() => import('./pages/NotesPage'))
const PlaylistsPage = lazy(() => import('./pages/PlaylistsPage'))
const PlaylistDetailPage = lazy(() => import('./pages/PlaylistDetailPage'))
const SharedPlaylistsPage = lazy(() => import('./pages/SharedPlaylistsPage'))
const SharedPlaylistPage = lazy(() => import('./pages/SharedPlaylistPage'))
const GamesPage = lazy(() => import('./pages/GamesPage'))
const GameSessionPage = lazy(() => import('./pages/GameSessionPage'))
const SurahReaderPage = lazy(() => import('./pages/SurahReaderPage'))
const SurahIndexPage = lazy(() => import('./pages/SurahIndexPage'))
const LearnPage = lazy(() => import('./pages/LearnPage'))


function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

/** Fires a GA4 page_view event on every route change (and once on mount
 *  for the initial route). Sibling of ScrollToTop so they share the same
 *  router-listener idiom. No-op in dev/test where GA isn't initialized. */
function AnalyticsPageviews() {
  const location = useLocation()
  useEffect(() => {
    trackPageview(location.pathname + location.search)
  }, [location.pathname, location.search])
  return null
}

function RouteFallback() {
  const { t } = useTranslation()
  return <LoadingSpinner message={t('common.loading')} />
}

// Picks between the standard home and the kid-mode home based on the current
// preference. Keeping both pages mounted via the same "/" route preserves
// the URL and lets a user re-share / bookmark a home link regardless of
// which mode they have on at the time.
function HomeOrKidHome() {
  const { isKidMode } = useKidMode()
  return isKidMode ? <KidHomePage /> : <HomePage />
}

function ReaderOrKidStage() {
  const { isKidMode } = useKidMode()
  return isKidMode ? <KidReaderStage /> : <QuranReaderPage />
}

// Persistent shell for public routes. Layout mounts ONCE and stays mounted
// across child route changes — only the <Outlet /> swaps. The Suspense sits
// inside the Layout so the rail/header stay visible while a lazy page chunk
// is fetched (just the content area shows the spinner).
//
// `key={pathname}` on the Suspense forces a full remount of the Outlet content
// on every path change. Without it, the playlist detail page would sometimes
// stick around visually after navigating away — React Router's Outlet was
// reusing the wrong fiber because the page used heavy useMemo'd state that
// blocked reconciliation. The audio engine lives ABOVE the Suspense (in
// PlaylistAudioProvider in main.tsx) so this remount doesn't interrupt
// playback.
function PublicLayoutRoute() {
  const location = useLocation()
  return (
    <Layout>
      <Suspense key={location.pathname} fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </Layout>
  )
}

// Same persistent-shell pattern, gated behind auth. While auth is loading we
// show the full-screen spinner (Layout isn't mounted yet); once authenticated,
// Layout mounts once and persists across protected route changes.
function ProtectedLayoutRoute() {
  const { user, loading } = useAuth()
  const { t } = useTranslation()
  const location = useLocation()

  if (loading) {
    return <LoadingSpinner fullScreen message={t('common.loading')} />
  }
  if (!user) {
    // Remember where the user was headed so a successful sign-in can return
    // them here instead of always landing on "/". Idempotent write during
    // render: same key, same value on every re-render of this branch (incl.
    // StrictMode's double-invoke), so it's safe without an effect.
    rememberReturnTo(location.pathname + location.search)
    return <Navigate to="/auth" replace />
  }
  return (
    <Layout>
      {/* Private, per-user surfaces (bookmarks, progress, notes, settings,
          playlists, games, memorization). Defense-in-depth alongside the
          robots.txt Disallow: a JS-rendering crawler that reaches one of
          these still sees noindex and won't index it. */}
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Suspense key={location.pathname} fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    </Layout>
  )
}

export default function App() {
  // Note: PreferencesProvider + PlaylistAudioProvider are mounted in main.tsx
  // above this component (so the playlist audio engine survives Routes
  // unmounting). App owns route declarations only.

  // Report PWA-install signals to GA4 (display_mode user property + the
  // `appinstalled` event). Mounted once here at the app root.
  usePwaInstallAnalytics()

  return (
    <>
      <ScrollToTop />
      <AnalyticsPageviews />
      <OfflineBootstrap />
      <KidRouteGuard>
      <Routes>
        {/* Bare-shell routes (auth flow) — no chrome */}
        <Route
          path="/auth"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AuthPage />
            </Suspense>
          }
        />
        <Route
          path="/auth/callback"
          element={
            <Suspense fallback={<RouteFallback />}>
              <AuthCallbackPage />
            </Suspense>
          }
        />

        {/* Public routes under a single persistent Layout. Home swaps to the
            kid-mode variant when prefs.kidMode is on — same URL ("/"), just a
            different component, so deep links from before the toggle keep
            working. */}
        <Route element={<PublicLayoutRoute />}>
          <Route path="/" element={<HomeOrKidHome />} />
          <Route path="/reader" element={<QuranReaderPage />} />
          <Route path="/reader/:surahNumber" element={<ReaderOrKidStage />} />
          {/* Crawl hub: one prerendered index per language linking to all 114
              surahs. Declared before the deeper surah route for readability;
              the paths have different segment counts so they never compete. */}
          <Route path="/:lang/surah" element={<SurahIndexPage />} />
          <Route path="/:lang/surah/:surahNumber/:slug" element={<SurahReaderPage />} />
        </Route>

        {/* Protected routes under a single persistent Layout */}
        <Route element={<ProtectedLayoutRoute />}>
          <Route path="/bookmarks" element={<BookmarksPage />} />
          <Route path="/bookmarks/:surahNumber" element={<BookmarkSurahPage />} />
          <Route path="/learn" element={<LearnPage />}>
            <Route index element={<LearnVerses />} />
            <Route path="practice" element={<LearnPractice />} />
            <Route path="stats" element={<LearnStats />} />
          </Route>
          <Route path="/memorization" element={<Navigate to="/learn" replace />} />
          <Route path="/progress" element={<ProgressPage />} />
          <Route path="/notes" element={<NotesPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/playlists" element={<PlaylistsPage />} />
          <Route path="/playlists/:id" element={<PlaylistDetailPage />} />
          <Route path="/shared" element={<SharedPlaylistsPage />} />
          <Route path="/shared/:playlistId" element={<SharedPlaylistPage />} />
          <Route path="/games" element={<GamesPage />} />
          <Route path="/games/:gameType" element={<GameSessionPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </KidRouteGuard>
    </>
  )
}


